import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Label } from "./Reveal";

const HERO_IMG =
  "https://images.unsplash.com/photo-1740679954227-a0cd19c042a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHx0b2RkbGVyJTIwZmFtaWx5JTIwaGFwcHklMjBtb3JuaW5nJTIwaG9tZXxlbnwwfHx8fDE3ODY4OTYxNTV8MA&ixlib=rb-4.1.0&q=85";

const LINES = ["Transform your Life", "and Parenting Journey"];

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ onBook, onConsult, onNav }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const badgeY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="hero-section"
      ref={ref}
      data-testid="hero-section"
      className="relative pt-28 lg:pt-36 pb-16 lg:pb-24 px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto"
    >
      <div className="grid grid-cols-12 gap-x-5 gap-y-10 lg:gap-8 items-center">
        {/* Headline — sits beside the image on mobile, left column on desktop */}
        <div className="col-span-7 lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 rounded-full border border-[#2F3B2E]/15 bg-white/60 px-3.5 py-1.5 mb-6"
          >
            <Sparkles size={13} className="text-[#A8916B] shrink-0" />
            <Label className="text-[10px] sm:text-xs">Grow Within. Parent Consciously. Thrive Together.</Label>
          </motion.div>

          <h1 data-testid="hero-headline" className="font-serif-display text-3xl sm:text-5xl lg:text-6xl font-medium tracking-tight leading-[1.08] text-[#3B352D]">
            {LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden">
                <motion.span
                  className="block"
                  custom={i}
                  variants={lineVariants}
                  initial="hidden"
                  animate="visible"
                >
                  {line}
                </motion.span>
              </span>
            ))}
          </h1>
        </div>

        {/* Image — beside headline on mobile, right column on desktop */}
        <div className="col-span-5 lg:col-span-6 lg:row-span-2 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="relative overflow-hidden clip-frame border border-[#2F3B2E]/15 shadow-[0_30px_60px_-20px_rgba(47,59,46,0.35)]"
          >
            <motion.img
              src={HERO_IMG}
              alt="A relaxed, happy mother with her child at home"
              style={{ y: imgY }}
              className="w-full h-44 sm:h-[420px] lg:h-[560px] object-cover scale-110"
            />
          </motion.div>

          <motion.div
            style={{ y: badgeY }}
            className="hidden sm:block absolute -left-3 sm:left-6 bottom-6 rounded-2xl bg-white/90 backdrop-blur px-5 py-4 border border-[#2F3B2E]/10 shadow-lg"
          >
            <p className="font-serif-display text-2xl text-[#2F3B2E]">98%</p>
            <p className="text-xs text-[#6B665C]">Family retention rate</p>
          </motion.div>
        </div>

        {/* Body copy, CTAs, stat — full width on mobile, left column on desktop */}
        <div className="col-span-12 lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="max-w-lg space-y-4 text-base sm:text-lg leading-relaxed text-[#6B665C]"
          >
            <p>
              Mended Alchemy is a purpose-led personal transformation and parenting venture founded by Priya Rawal. As an edupreneur, Priya brings together her experience in leadership, life coaching and conscious parenting to help individuals grow and parents navigate the journey of raising children with greater awareness.
            </p>
            <p>
              Our core services focus on Personal Transformation for Mothers and Conscious Parenting for Parents, supported by thoughtfully designed digital products and educational programmes that make meaningful learning and growth accessible beyond one-to-one coaching.
            </p>
            <p className="font-serif-display italic text-[#2F3B2E]">
              Because when we become more conscious of ourselves, we create more conscious relationships and a stronger foundation for children to thrive.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-9 flex flex-col sm:flex-row sm:flex-wrap gap-3.5"
          >
            <button
              onClick={onConsult}
              data-testid="hero-consult-button"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#2F3B2E] px-7 py-3.5 text-sm font-medium text-white hover:bg-[#232B22] hover:text-white transition-colors duration-200"
            >
              Book a Consultation
              <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" />
            </button>
            <button
              onClick={onBook}
              data-testid="hero-book-button"
              className="inline-flex items-center justify-center rounded-full border border-[#A8916B] bg-[#A8916B]/10 px-7 py-3.5 text-sm font-medium text-[#2F3B2E] hover:bg-[#A8916B] hover:text-white transition-colors duration-200"
            >
              Book Free Discovery Call
            </button>
            <button
              onClick={() => onNav("#ebook-section")}
              data-testid="hero-ebook-button"
              className="inline-flex items-center justify-center rounded-full border border-[#2F3B2E]/25 px-7 py-3.5 text-sm font-medium text-[#2F3B2E] hover:bg-[#2F3B2E] hover:text-white transition-colors duration-200"
            >
              Get the Ebook
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1 }}
            className="mt-10"
          >
            <p className="font-serif-display text-3xl text-[#2F3B2E]">15+</p>
            <p className="text-xs text-[#6B665C] mt-1">Years of life experience</p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
